/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */
import React from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet';
import {Script, Link} from 'gatsby';

import ncOrganizationSchema from '@ThemesApp/common/constants/schema/organization/nc';
import mgOrganizationSchema from '@ThemesApp/common/constants/schema/organization/mg';

import {aggregateRating} from '@ThemesApp/common/structuredData/rating';
import {breadcrumbListType} from '@ThemesApp/common/structuredData/breadcrumbListType';
import {faqPage} from '@ThemesApp/common/structuredData/faqPage';
import {webPage} from '@ThemesApp/common/structuredData/webPage';
import {article} from '@ThemesApp/common/structuredData/article';
import {organizationType} from '@ThemesApp/common/structuredData/organizationType';
import {personType} from '@ThemesApp/common/structuredData/personType';
import {siteName} from '@ThemesApp/common/structuredData/siteName';

import {useSiteMetadata} from '@/hooks/useSiteMetadata';

function Seo({
               description = '', lang = 'pl', meta = [], title, ogImage, ogType, structuredData, vwo, hasNoindex, hrefLang
             }) {
  const siteMetadata = useSiteMetadata();

  const metaDescription = description || siteMetadata.description;
  const defaultTitle = siteMetadata.title;
  // const ogUrl = typeof window !== 'undefined' ? window.location.href : null;

  const schemaArticles = [];

  if (structuredData?.webPage) {
    schemaArticles.push(webPage(structuredData.webPage));
  }

  if (structuredData?.article) {
    schemaArticles.push(article(structuredData.article));
  }

  if (structuredData?.personTypeAuthor) {
    schemaArticles.push(personType(structuredData.personTypeAuthor));
  }

  if (structuredData?.personTypeEditor) {
    schemaArticles.push(personType(structuredData.personTypeEditor));
  }

  return (
    <><Helmet
      htmlAttributes={{
        lang,
      }}
      title={title || null}
      titleTemplate={defaultTitle ? '%s' : null}
      meta={[
        {
          name: 'description',
          content: metaDescription,
        },
        {
          ...(hasNoindex && {
            name: 'robots',
            value: 'noindex'
          })
        },
        {
          property: 'og:title',
          content: title || null,
        },
        {
          property: 'og:site_name',
          content: 'natu.care',
        },
        // {
        //   property: 'og:url',
        //   content: ogUrl,
        // },
        {
          property: 'og:image',
          content: ogImage ? `${ogImage}?w=1200\u0026h=630` : 'https://natu.care/images/natucare.png',
        },
        {
          property: 'og:image:alt',
          content: title || null,
        },
        {
          property: 'og:description',
          content: metaDescription || null,
        },
        {
          property: 'og:type',
          content: ogType ? ogType : 'website',
        },
        {
          name: 'twitter:card',
          content: 'summary',
        },
        {
          name: 'twitter:creator',
          content: siteMetadata?.author || '',
        },
        {
          name: 'twitter:title',
          content: title || null,
        },
        {
          name: 'twitter:description',
          content: metaDescription,
        },
        {
          property: 'twitter:image',
          content: ogImage ? `${ogImage}?w=1200\u0026h=630` : 'https://natu.care/images/natucare.png',
        },
        {
          property: 'twitter:image:alt',
          content: title || null,
        },
      ].concat(meta)}
    >

      {(vwo && typeof vwo !== 'undefined') && <link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />}
      {hrefLang?.map(({ href, locale}) => <link rel="alternate" href={href} hrefLang={locale} key={locale} />)}    
    </Helmet>

      {(vwo && typeof vwo !== 'undefined') && <Script strategy='idle'
                                                      id='vwoCode'>
                                                        {
                                                        `window._vwo_code || (function() { var account_id=923539, version=2.1, settings_tolerance=2000, hide_element='body', hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important', /* DO NOT EDIT BELOW THIS LINE */ f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement('script'),r=this;t=t||{};if(i){n.textContent=i;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var i=this.hide_element(),n=i?i+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var n=d.createElement('div');n.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';n.setAttribute('id','_vis_opt_path_hides');n.classList.add('_vis_hide_layer');t.parentNode.insertBefore(n,t.nextSibling)}var o='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:o})}else{this.load(o+'&x=true')}}};w._vwo_code=code;code.init();})();`
                                                        }
                                                      </Script>
      }
      <Script strategy='idle' type='application/ld+json'>{organizationType({
        ...ncOrganizationSchema,
        parentOrganization: organizationType(mgOrganizationSchema)
      })}</Script>
      {structuredData?.rating &&
        <Script strategy='idle' type='application/ld+json'>{aggregateRating(structuredData.rating)}</Script>
      }
      {structuredData?.breadcrumbs &&
        <Script strategy='idle' type='application/ld+json'>{breadcrumbListType(structuredData.breadcrumbs)}</Script>
      }
      {structuredData?.faqPage &&
        <Script strategy='idle' type='application/ld+json'>{faqPage(structuredData.faqPage)}</Script>
      }
      {structuredData?.siteName &&
        <Script strategy='idle' type='application/ld+json'>{siteName(structuredData.siteName)}</Script>
      }
      {(schemaArticles && schemaArticles.length > 0) &&
        <Script strategy='idle' type='application/ld+json'>{`{
            "@context": "https://schema.org",
            "@graph": [ ${schemaArticles.join(',')} ]
          }`}</Script>
      }
    </>

  );
}

Seo.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
};

export default Seo;
